import { useTranslation } from 'next-i18next';
import { Label, Input, RadioText } from '@components/common/Forms';
import { GenerateFields } from '@components/common/Forms/GenerateFields/GenerateFields';
import { MixedFormFilterProps } from '@type/filters';
import { queryFields } from '@components/features/filters/helpers/filterObjects';


/**
 * @description Generates the fields for the Search In filter group.
 * * **recordFunc** - the 'record' function from useBjForm.
 * * **submitFunc** - the function to trigger the form submit. You might want to trigger it on click/blur. Defaults to undefined.
 * * **verticalSpacing** - the spacing between the items. Defaults to 'gap-4'.
 * * **verticalSpacingNested** - the spacing between the nested items. Defaults to 'my-1.5'.
 * instead of the default one.
 * * **idPrefix** - the prefix for the form elements ids. When using multiple forms on the same page,
 * this is needed to avoid duplicate ids.
 * **customLabelStyles** - the custom styles for the labels.
 * **customLabelStylesNested** - the custom styles for the nested labels.
 * **customLabelStylesNestedWrapper** - the custom styles for the nested labels wrapper.
 * **titleStyles** - the custom styles for the title.
 */
export const SearchInForm = (props: MixedFormFilterProps) => {
  // Destructure props
  const {
    recordFunc,
    submitFunc,
    verticalSpacing = 'gap-2',
    verticalSpacingNested,
    idPrefix,
    customLabelStyles,
    customLabelStylesNested,
    customLabelStylesNestedWrapper,
    titleStyles,
  } = props;

  // Translation
  const { t } = useTranslation('common');


  // Render component
  // ***********************************************
  return (
    <div className={`grid ${verticalSpacing}`}>
      {/* Separator title */}
      <div className={titleStyles || 'mb-2 text-sm font-bold text-ink-medium'}>
        {t('search-in-page-intro')}
      </div>


      {/* Target fields */}
      <div className={`grid ${verticalSpacing}`}>
        {/* Candidate name */}
        <Label htmlFor={`${idPrefix || ''}target-2`} defaultMargin={false} className={`flex cursor-pointer items-center ${customLabelStyles || ''}`}>
          <Input
            type="radio"
            {...(submitFunc ? { onClick: submitFunc } : {})}
            {...recordFunc('nameSearch', {
              id: `${idPrefix || ''}nameSearch-1`,
              defaultValue: '1',
              filtersLabel: 'global.filter.candidate-name',
            })}
          />
          <RadioText className="pt-px">{t('global.filter.candidate-name')}</RadioText>
        </Label>

        {/* Experience */}
        <Label htmlFor={`${idPrefix || ''}target-1`} defaultMargin={false} className={`peer flex cursor-pointer items-center ${customLabelStyles || ''}`}>
          <Input
            type="radio"
            {...(submitFunc ? { onClick: submitFunc } : {})}
            {...recordFunc('nameSearch', {
              id: `${idPrefix || ''}nameSearch-2`,
              defaultValue: '2',
              filtersLabel: 'global.filter.experience-label',
              removeFieldsIfThisUnchecked: ['queryFields'],
            })}
          />
          <RadioText className="pt-px">{t('global.filter.experience-label')}</RadioText>
        </Label>

        {/* Experience query fields + last: show if 'experience' radio is checked */}
        <div className={`-my-1.5 hidden pl-6 peer-has-[:checked]:block ${customLabelStylesNestedWrapper || ''}`}>
          {/* Query fields */}
          <GenerateFields
            recordFunc={recordFunc}
            submitFunc={submitFunc}
            filterObject={queryFields}
            spacing={verticalSpacingNested || customLabelStylesNested}
            idPrefix={idPrefix}
          />
        </div>
      </div>

      {/* Present experience */}
      <Label htmlFor={`${idPrefix || ''}presentExperience`} defaultMargin={false} className={`flex cursor-pointer items-center ${customLabelStyles || ''}`}>
        <Input
          type="checkbox"
          {...(submitFunc ? { onClick: submitFunc } : {})}
          {...recordFunc('presentExperience', {
            id: `${idPrefix || ''}presentExperience`,
            defaultValue: 1,
            filtersLabel: 'resume.filter.label.experience-last',
          })}
        />
        <RadioText className="pt-px">{t('resume.filter.label.experience-last')}</RadioText>
      </Label>
    </div>
  );
};
